import React, { useState } from 'react';
import { Phone, Mail, MapPin, User, ShieldCheck, ArrowRight, CheckCircle } from 'lucide-react';
import { Verify } from 'react-puzzle-captcha';
import 'react-puzzle-captcha/dist/react-puzzle-captcha.css';

const CONTACT_API = 'https://fundvisercapital.in/app/contact-api.php';

const ContactSection = () => {
  const [form, setForm]               = useState({ name: '', email: '', phone: '', message: '' });
  const [captchaChecked, setCaptcha]  = useState(false);
  const [captchaKey, setCaptchaKey]   = useState(0);
  const [loading, setLoading]         = useState(false);
  const [success, setSuccess]         = useState('');
  const [error, setError]             = useState('');

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setSuccess(''); setError('');
    if (!captchaChecked) { setError('Please confirm you are not a robot.'); return; }
    setLoading(true);
    try {
      const res  = await fetch(CONTACT_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); }
      catch { setError(`Server error: ${text.slice(0, 120)}`); return; }
      if (data.success) {
        setSuccess(data.message);
        setForm({ name: '', email: '', phone: '', message: '' });
        setCaptcha(false);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError(`Network error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#360153] focus:bg-white focus:ring-4 focus:ring-[#360153]/8 transition-all duration-200";

  return (
    <section className="py-24 relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #f8f4ff 0%, #fff 50%, #f0f4ff 100%)' }}>

      {/* Background blobs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(54,1,83,0.06) 0%, transparent 70%)', transform: 'translate(30%, -30%)' }} />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(54,1,83,0.05) 0%, transparent 70%)', transform: 'translate(-30%, 30%)' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* ── Header ── */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-black tracking-widest uppercase mb-6"
            style={{ background: 'rgba(54,1,83,0.08)', color: '#360153' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-[#360153] animate-pulse" />
            Get In Touch
          </span>
          <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-5 leading-tight">
            Let's Start a<br />
            <span className="text-[#360153]">Conversation</span>
          </h2>
          <p className="text-base text-gray-500 max-w-xl mx-auto leading-relaxed">
            A BSE-listed company driven by strategic expertise, Our team is here to help you explore the right opportunities and achieve your financial goals.
          </p>
        </div>

        {/* ── Two columns ── */}
        <div className="grid lg:grid-cols-8 gap-8 items-stretch">

          {/* ── Left: dark info panel (4/8) ── */}
          <div className="lg:col-span-4 flex flex-col rounded-3xl overflow-hidden"
            style={{ background: 'linear-gradient(145deg, #1a002b 0%, #360153 50%, #2d0147 100%)' }}>

            <div className="p-8 flex-1 flex flex-col">
              <p className="text-white/40 text-s font-black tracking-widest uppercase mb-2">Contact Information</p>
              <h3 className="text-white text-2xl font-black mb-8 leading-snug">
                We’re always here to help!
              </h3>

              {/* Contact items */}
              <div className="space-y-6 flex-1">
                {[
                  { icon: Phone, label: 'Call Us', value: '+91-22-31236586', href: 'tel:+912231236586' },
                  { icon: Mail,  label: 'Email Us', value: 'info@fundvisercapital.in', href: 'mailto:info@fundvisercapital.in' },
                ].map(({ icon: Icon, label, value, href }) => (
                  <a key={label} href={href}
                    className="flex items-center gap-4 group">
                    <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 transition-all duration-200 group-hover:scale-110"
                      style={{ background: 'rgba(255,255,255,0.12)' }}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-white/40 text-xs font-bold uppercase tracking-widest">{label}</p>
                      <p className="text-white text-base font-semibold group-hover:text-yellow-300 transition-colors">{value}</p>
                    </div>
                  </a>
                ))}

                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(255,255,255,0.12)' }}>
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-white/40 text-xs font-bold uppercase tracking-widest mb-0.5">Visit Us</p>
                    <p className="text-white text-base font-semibold leading-relaxed">
                      22/7, Manek Mahal, 90 Veer Nariman Road,<br />
                      Churchgate, Mumbai 400020,<br />
                      Maharashtra, India
                    </p>
                  </div>
                </div>
              </div>

              {/* Decorative circles */}
              <div className="relative h-24 mt-8 overflow-hidden pointer-events-none">
                <div className="absolute -bottom-8 -right-8 w-40 h-40 rounded-full"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }} />
                <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }} />
              </div>
            </div>

            {/* Grievance section */}
            <div className="mx-4 mb-4 rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                <p className="text-yellow-400 text-xs font-black uppercase tracking-widest">Company's Designated Investor Grievance Officer</p>
              </div>
              <div className="space-y-2.5">
                {[
                  { icon: User,       text: 'Mr. Raujesh Khandelwal' },
                  { icon: ShieldCheck,text: 'Company Secretary & Compliance Officer' },
                  { icon: Mail,       text: 'info@fundvisercapital.in', href: 'mailto:info@fundvisercapital.in' },
                  { icon: Phone,      text: '+91-22-31236586', href: 'tel:+912231236586' },
                ].map(({ icon: Icon, text, href }, i) => (
                  href
                    ? <a key={i} href={href} className="flex items-center gap-2.5 group">
                        <Icon className="w-4 h-4 text-white/40 flex-shrink-0" />
                        <span className="text-white/70 text-sm group-hover:text-white transition-colors">{text}</span>
                      </a>
                    : <div key={i} className="flex items-center gap-2.5">
                        <Icon className="w-4 h-4 text-white/40 flex-shrink-0" />
                        <span className="text-white/70 text-sm">{text}</span>
                      </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Right: Form (4/8) ── */}
          <div className="lg:col-span-4 bg-white rounded-3xl shadow-2xl shadow-gray-200/80 p-8 flex flex-col" style={{ border: '1px solid #f0e8f8' }}>

            <div className="mb-7">
              <h3 className="text-2xl font-black text-gray-900">Send us a Message</h3>
              <p className="text-sm text-gray-400 mt-1">We'll get back to you within 24 hours.</p>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 gap-5">

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Full Name *</label>
                  <input type="text" name="name" value={form.name} onChange={handleChange} required
                    className={inputCls} placeholder="John Doe" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Phone Number</label>
                  <input type="tel" name="phone" value={form.phone} onChange={handleChange}
                    className={inputCls} placeholder="+91 98765 43210" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Email Address *</label>
                <input type="email" name="email" value={form.email} onChange={handleChange} required
                  className={inputCls} placeholder="john@example.com" />
              </div>

              <div className="flex-1 flex flex-col">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Message *</label>
                <textarea name="message" value={form.message} onChange={handleChange} required
                  className={`${inputCls} flex-1 min-h-[140px] resize-none`}
                  placeholder="Enter your message here..." />
              </div>

              {/* CAPTCHA */}
              {captchaChecked ? (
                <div className="flex items-center gap-3 px-4 py-3.5 rounded-xl"
                  style={{ background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', border: '1.5px solid #86efac' }}>
                  <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-green-800">Verified Successfully</p>
                    <p className="text-xs text-green-600">You're confirmed as human</p>
                  </div>
                  <button type="button"
                    onClick={() => { setCaptcha(false); setCaptchaKey(k => k + 1); }}
                    className="text-xs text-green-500 hover:text-green-700 font-medium transition-colors">
                    Reset
                  </button>
                </div>
              ) : (
                <button type="button"
                  onClick={() => setCaptchaKey(k => k + 1)}
                  className="w-full flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all duration-200 hover:shadow-md active:scale-[0.99] group"
                  style={{ background: '#f8f4ff', border: '1.5px solid #e8d8f8' }}
                  data-captcha-trigger="true">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform duration-200 group-hover:scale-110"
                    style={{ background: 'rgba(54,1,83,0.1)' }}>
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#360153" strokeWidth="2.5">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold text-gray-700 flex-1 text-left">I'm not a robot</span>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[10px] font-black text-gray-400 leading-none tracking-wide">CLICK & VERIFY</p>
                    <p className="text-[9px] text-gray-300 mt-0.5">Slide to confirm</p>
                  </div>
                </button>
              )}

              {/* Puzzle Modal Overlay */}
              {!captchaChecked && captchaKey > 0 && (
                <div className="fixed inset-0 z-50 flex items-center justify-center"
                  style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(6px)' }}
                  onClick={e => { if (e.target === e.currentTarget) setCaptchaKey(0); }}>
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl"
                    style={{ background: '#fff', border: '1px solid #e8d8f8', maxWidth: '95vw' }}>
                    {/* Modal header */}
                    <div className="flex items-center justify-between px-5 py-3.5"
                      style={{ background: 'linear-gradient(135deg, #1a002b, #360153)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                      <div className="flex items-center gap-2.5">
                        <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        </svg>
                        <span className="text-white text-sm font-bold">Security Verification</span>
                      </div>
                      <button type="button" onClick={() => setCaptchaKey(0)}
                        className="text-white/50 hover:text-white transition-colors text-lg leading-none">✕</button>
                    </div>
                    {/* Puzzle */}
                    <div className="p-1">
                      <Verify
                        key={captchaKey}
                        width={320}
                        height={160}
                        visible={true}
                        onSuccess={() => { setCaptcha(true); setCaptchaKey(0); }}
                        onFail={() => setCaptcha(false)}
                        onRefresh={() => setCaptchaKey(k => k + 1)}
                      />
                    </div>
                    <p className="text-center text-[10px] text-gray-400 pb-3">Drag the slider to complete the puzzle</p>
                  </div>
                </div>
              )}

              {/* Feedback */}
              {error   && <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3"><span className="text-red-500 text-sm">⚠</span><p className="text-sm text-red-600 font-medium">{error}</p></div>}
              {success && <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3"><span className="text-green-600 text-sm">✓</span><p className="text-sm text-green-700 font-medium">{success}</p></div>}

              <button type="submit" disabled={loading}
                className="mt-auto w-full flex items-center justify-center gap-3 py-4 rounded-2xl text-white text-sm font-black tracking-wide transition-all duration-300 hover:scale-[1.02] hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                style={{ background: 'linear-gradient(135deg, #360153 0%, #5a0280 100%)', boxShadow: '0 8px 24px rgba(54,1,83,0.35)' }}>
                {loading ? (
                  <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Sending…</>
                ) : (
                  <>Send Message <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* ── Embedded Map ── */}
        <div className="mt-10 rounded-3xl overflow-hidden shadow-xl" style={{ border: '1px solid #e8d8f8' }}>
          <iframe
            title="Fundviser Capital Office Location"
            src="https://maps.google.com/maps?ll=18.933749,72.824325&z=20&t=m&q=22/7,+Manek+Mahal,+90+Veer+Nariman+Road,+Churchgate,+Mumbai+400020&output=embed"
            width="100%" height="380"
            style={{ border: 0, display: 'block' }}
            allowFullScreen loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>

      </div>
    </section>
  );
};

export default ContactSection;
